/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { useIsFetching } from '@tanstack/react-query';
import React, { ReactNode, useState } from 'react';

import { useAccount } from '~/apis';
import { CookieConsentBanner, useTranslation } from '~/app';
import { config } from '~/app/config';
import { ROUTE_IMPRESSUM, ROUTE_PRIVACY, ROUTE_TERMS } from '~/app/routes';
import { useAuthorizedDispatchOnMount } from '~/app/useAuthReady';
import { AuthDialogManager } from '~/auth';
import ClientOnly, { useHasMounted } from '~/components/ClientOnly';
import LoadingBar from '~/components/LoadingBar';
import VersionInfoPopover from '~/components/VersionInfoPopover';
import startscreenImg from '~/images/startscreen.jpg';
import { rejoinGame } from '~/setup';
import { cn } from '~/utils/styles';

import AccountMenuButton from './AccountMenu';
import {
  DeveloperButton,
  DiscordButton,
  InstructionsButton,
  RejoinButton,
  TableButton,
  ToplistButton,
  WhatsAppButton,
  YouTubeButton,
} from './HeaderButton';
import HeaderOverflowMenu from './HeaderOverflowMenu';
import HomeButton from './HomeButton';
import LanguageButton from './LanguageButton';
import { LogoutDialogProvider } from './LogoutDialogContext';

interface ContainerProps {
  /**
   * Will be shown in browser tab and set as SEO page title.
   * Header is used as a fallback if not set so this is totally optional.
   */
  title?: string;

  /**
   * Shown in header bar next to optional back button
   */
  header?: string;

  /**
   * Optional icon (e.g. lock) shown before the header title
   */
  headerIcon?: ReactNode;

  /**
   * Optional badge label (e.g. "Beta") shown as diagonal ribbon when config.showBetaHint is true
   */
  headerBadge?: string;

  hideBackButton?: boolean;

  /**
   * Make header invisible
   */
  hideHeader?: boolean;

  translucentHeader?: boolean;

  children: ReactNode | ReactNode[];

  className?: string;

  disableBackgroundBlur?: boolean;

  responsiveVerticalHeader?: boolean;

  /** Use full page width instead of constrained container (e.g. for table/toplist pages). */
  fullWidth?: boolean;
}

const Container = ({
  header,
  headerIcon,
  headerBadge,
  title: _title = header,
  children,
  className,
  hideBackButton,
  hideHeader,
  translucentHeader,
  disableBackgroundBlur,
  responsiveVerticalHeader,
  fullWidth = false,
}: ContainerProps): JSX.Element => {
  const hasMounted = useHasMounted();
  const { t, urlPath } = useTranslation();
  const isFetching = useIsFetching();
  const [cookieDialogOpen, setCookieDialogOpen] = useState(false);
  const { isSuccess } = useAccount();
  const shouldAutoProbeRejoin =
    urlPath === '/' ||
    urlPath === '/account' ||
    urlPath === '/account/subscriptions' ||
    urlPath === '/table' ||
    urlPath === '/toplist';

  // Single source for rejoin probe: dispatch once when auth is ready so header buttons (RejoinButton, HeaderOverflowMenu) only read rejoinState.
  useAuthorizedDispatchOnMount(isSuccess && shouldAutoProbeRejoin ? rejoinGame() : null);

  // Use isFetching only after mount so server and first client paint match (avoids hydration mismatch)
  const isFetchingForBar = hasMounted ? isFetching : 0;

  return (
    <LogoutDialogProvider>
      {!hideHeader && (
        <nav
          role="navigation"
          className={cn('sticky top-0 z-30 h-16', {
            'bg-primary shadow-md': !translucentHeader,
          })}
        >
          <div
            className={cn(
              'flex h-full flex-row flex-nowrap items-center justify-between space-x-1 text-white',
              hideBackButton ? 'px-4' : 'pr-4',
            )}
          >
            {!hideBackButton && <HomeButton className="ml-2 h-full w-14 shrink-0 p-4" />}

            <div
              className={cn('flex min-w-0 overflow-visible', header ? 'min-w-0 flex-1' : 'hidden')}
            >
              <div className="flex w-fit max-w-full min-w-0 flex-row items-center gap-2 overflow-visible">
                <h1 className="min-w-0 flex-1 truncate text-lg">{header ?? t('button_menu')}</h1>
                {headerIcon}
                {header && headerBadge && config.showBetaHint && (
                  <span
                    className="-ml-3 shrink-0 origin-center translate-y-1 rotate-[-35deg] bg-amber-400 px-2 py-0.5 text-xs font-semibold text-amber-950 shadow-sm"
                    aria-label={headerBadge}
                  >
                    {headerBadge}
                  </span>
                )}
              </div>
            </div>

            {/* Desktop (md+): inline buttons */}
            <div
              className={cn('hidden shrink-0 flex-row flex-nowrap space-x-2 md:flex', {
                'flex-1 justify-end': !responsiveVerticalHeader,
                'flex-none': responsiveVerticalHeader,
              })}
            >
              {(urlPath === '/' ||
                urlPath === '/account' ||
                urlPath === '/account/subscriptions' ||
                urlPath === '/table' ||
                urlPath === '/toplist') && <RejoinButton />}

              <TableButton />

              <ToplistButton />

              <InstructionsButton />

              <YouTubeButton />

              <DiscordButton />

              <WhatsAppButton />

              <DeveloperButton />

              <LanguageButton />

              <AccountMenuButton />
            </div>

            {/* Mobile (< md): overflow menu */}
            <div className="flex shrink-0 md:hidden">
              <HeaderOverflowMenu />
            </div>
          </div>

          {!translucentHeader && (
            <LoadingBar
              isLoading={isFetchingForBar > 0}
              className="h-1 w-full shadow-md transition-opacity"
            />
          )}
        </nav>
      )}

      <main
        role="main"
        className={cn(
          'relative flex flex-col',
          // When we render the sticky header, it already consumes vertical space in normal flow.
          // Using 100vh here would add nav height on top and cause unwanted scrolling.
          hideHeader ? 'min-h-screen' : 'min-h-[calc(100dvh-4rem)]',
        )}
      >
        <img
          src={startscreenImg}
          alt={t('start_imageAlt')}
          className={cn(
            'bg-image-edge-fade pointer-events-none fixed inset-0 h-screen w-screen overflow-hidden bg-primary object-contain opacity-30',
            !disableBackgroundBlur && 'blur-lg',
          )}
        />

        {/* Layered scrim: light global wash + stronger top gradient for readability */}
        <div className="pointer-events-none fixed inset-0 bg-black/25" aria-hidden />
        <div
          className="pointer-events-none fixed inset-x-0 top-0 h-32 bg-linear-to-b from-black/40 to-transparent"
          aria-hidden
        />

        <div
          className={cn(
            'relative mx-auto flex min-h-0 flex-1 flex-col items-stretch p-4 sm:p-6 lg:p-8',
            fullWidth ? 'w-full max-w-none' : 'container',
            className,
          )}
        >
          {children}
        </div>

        {urlPath === '/' && !cookieDialogOpen && (
          <div className="relative mt-6 mb-4 flex w-full flex-row items-end justify-between gap-4 px-4 sm:px-6 lg:px-8">
            <div className="flex min-w-0 flex-1 flex-col gap-4 text-sm sm:flex-row sm:flex-nowrap">
              <a
                href={ROUTE_TERMS}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-secondary-light min-w-0 truncate text-secondary underline"
              >
                {t('agbs_title')}
              </a>
              <a
                href={ROUTE_PRIVACY}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-secondary-light min-w-0 truncate text-secondary underline"
              >
                {t('datenschutz_title')}
              </a>
              <a
                href={ROUTE_IMPRESSUM}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-secondary-light min-w-0 truncate text-secondary underline"
              >
                {t('impressum_title')}
              </a>
            </div>
            <div className="shrink-0">
              <VersionInfoPopover />
            </div>
          </div>
        )}

        <AuthDialogManager />
      </main>

      <div role="region" aria-label="Cookie Banner">
        <ClientOnly>
          <CookieConsentBanner onDialogOpenChange={setCookieDialogOpen} />
        </ClientOnly>
      </div>
    </LogoutDialogProvider>
  );
};

export default Container;
